import { MexicoMarketType, PortOfEntryType } from "@/lib/types"
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  QueueListIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline"
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react"

type SearchListType = {
  id: string
  name: string
}[]

type DropdownSearch = {
  id: string
  name: string
}

type Props = {
  title: string
  selectedIds: {
    id: string
    name: string
  }[]
  selectedList: {
    id: string
    name: string
  }[]
  setSelectedList: Dispatch<SetStateAction<SearchListType>>
  listingData: { data: any[] }
}

export const MultiSelectDropdown: FC<Props> = (props) => {
  const [input, setInput] = useState("")
  const dropdown = useRef<any>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [count, setCount] = useState(0)
  const [filteredData, setFilteredData] = useState<DropdownSearch[]>([])
  const [selectedItems, setSelectedItems] = useState<DropdownSearch[]>([])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdown.current && !dropdown.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [dropdown, isOpen])

  const toggleDropdown = () => {
    setCount((prevClickCount) => prevClickCount + 1)
    setIsOpen(count % 2 === 0)
  }

  useEffect(() => {
    const filtered = props.selectedIds.filter((selectedId) => {
      return props.selectedList.filter((e) => e.id === selectedId.id)
    })
    props.setSelectedList(filtered)
  }, [])

  useEffect(() => {
    if (input) {
      setFilteredData(
        props.listingData.data.filter((item) =>
          item.name.toLowerCase().includes(input.toLowerCase()),
        ),
      )
    } else {
      setFilteredData(props.listingData.data)
    }
  }, [input, props.listingData.data])

  const handleSearch = (value: string) => {
    setInput(value)
  }

  const handleSelectItem = (item: DropdownSearch) => {
    if (!selectedItems.find((selected) => selected.id === item.id)) {
      setSelectedItems((prev) => [...prev, item])
    }
    setInput("")
    setFilteredData(props.listingData.data)
  }

  const onAssign = (item: DropdownSearch) => {
    const isExist = props.selectedList.some((e) => {
      return e.id === item.id
    })
    if (isExist == true) {
      const filtered = props.selectedList.filter(
        (selected) => selected.id !== item.id,
      )
      props.setSelectedList(filtered)
    } else {
      props.setSelectedList((prev) => [...prev, item])
    }
  }

  return (
    <div>
      <label className="text-xs dark:text-primary-8 text-light-1">
        {props.title}
      </label>
      <div>
        <div className="relative dark:text-primary-1 text-light-2">
          <div className="flex">
            <div className="flex justify-between items-center rounded-full text-sm w-full">
              <div
                className="px-1 py-2 h-auto dark:bg-transparent bg-white flex justify-between items-center gap-2 w-full border-2 dark:border-secondary-2 rounded mt-1"
                onClick={toggleDropdown}
              >
                <span
                  className={
                    props.selectedList && props.selectedList.length !== 0
                      ? "hidden"
                      : "dark:text-primary-2 text-light-2 px-2"
                  }
                >
                  {props.selectedList && props.selectedList.length !== 0
                    ? ""
                    : "Select"}
                </span>
                <div className="grid md:grid-cols-3 grid-cols-2 gap-2">
                  {props.selectedList.map((e, i) => (
                    <div
                      className="flex justify-between gap-4 items-center rounded-full dark:bg-secondary-2 bg-white border dark:border-zinc-500/25 text-xs px-3 py-1"
                      key={i}
                    >
                      <span className="dark:text-primary-2 text-light-2 text-xs truncate w-auto">
                        {e.name}
                      </span>
                      <div className="">
                        <XCircleIcon
                          className="h-4 w-4 text-primary-3 cursor-pointer"
                          onClick={() =>
                            props.setSelectedList(
                              props.selectedList.filter(
                                (entry) => entry.id !== e.id,
                              ),
                            )
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <ChevronDownIcon className="w-4 h-4 dark:text-primary-2 text-light-2" />
                </div>
              </div>
            </div>
          </div>
          {isOpen && (
            <div className="mt-0.5 max-h-48 h-48 w-full border-2 dark:border-secondary-2 dark:bg-primary-1 bg-white text-primary-2 rounded text-sm font-normal focus:outline-none z-10 ">
              <div className="flex items-center px-2 h-97 border-b dark:border-secondary-2">
                <input
                  className="w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white text-light-2"
                  type="text"
                  placeholder="Search"
                  value={input}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <MagnifyingGlassIcon className="w-4 h-4 text-white" />
              </div>
              <div className="md:max-h-36 max-h-24 overflow-auto divide-y divide-primary-4/40 h-full">
                {filteredData
                  ?.slice()
                  .sort((a, b) => {
                    const isASelected: any = props.selectedList.some(
                      (e) => e.id === a.id,
                    )
                    const isBSelected: any = props.selectedList.some(
                      (e) => e.id === b.id,
                    )
                    return isBSelected - isASelected
                  })
                  .map((point, i) => (
                    <div
                      key={i}
                      className={
                        (props.selectedList.some((e) => point.id == e.id)
                          ? "bg-white/5 text-primary-3"
                          : "hover:bg-primary-1 dark:text-primary-2 text-light-2") +
                        " flex items-center gap-2 hover:bg-white/5"
                      }
                    >
                      <label
                        className={
                          "flex gap-3 w-full truncate px-2 py-3 text-xs font-normal capitalize"
                        }
                        htmlFor={`${point.id}`}
                      >
                        <input
                          className="before:bg-zinc-300 portOfEntry-checkbox"
                          type="checkbox"
                          value={point.id}
                          name={point.name}
                          id={`${point.id}`}
                          onChange={(e) => {
                            onAssign({ id: e.target.value, name: point.name })
                            handleSelectItem(point)
                          }}
                          checked={props.selectedList.some(
                            (e) => e.id == point.id,
                          )}
                        />
                        {point.name}
                      </label>
                    </div>
                  ))}
                {filteredData?.length === 0 && (
                  <div className="flex py-2 items-center h-full">
                    <span className="text-xs text-primary-2 mx-auto">
                      Not found
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
